/**
 * What the search box says about the list under it.
 *
 * The WAI-ARIA combobox pattern, and all of it set from the script. The box
 * says it controls a listbox, whether the listbox is open and which option is
 * current, and `suggestion-option.ts` says which option is selected. Markup a
 * reader with no script gets therefore claims only what is true of it, the
 * theme drawing a plain `<input type="search">` and nothing else.
 *
 * A module of its own because it is the one part of this that a reader hearing
 * the page depends on. `suggestion-list.ts` is what the rest of them see.
 *
 * **The ids count the boxes**, because a page can carry two: one in the rail
 * and one in the article. An id is unique to a document, and two lists sharing
 * one would leave `aria-controls` on both boxes pointing at whichever the
 * browser found first.
 */

/** One list's id, which is what its box points `aria-controls` at. */
export function listId(box: number): string {
  return `cb-search-suggestions-${String(box)}`;
}

/** One option's id, as `aria-activedescendant` names it. */
export function optionId(box: number, at: number): string {
  return `cb-search-suggestion-${String(box)}-${String(at)}`;
}

/** Say that this box has a list of suggestions behind it, closed for now. */
export function openCombobox(input: HTMLInputElement, box: number): void {
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-controls", listId(box));
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-expanded", "false");
}

/**
 * Say whether the list is open, and put the cursor back in the box.
 *
 * The two go together every time. A list that has just been drawn or just been
 * put away has no current option either way, and an `aria-activedescendant`
 * left behind would name an element that is gone.
 */
export function expandCombobox(input: HTMLInputElement, open: boolean): void {
  input.setAttribute("aria-expanded", String(open));
  input.removeAttribute("aria-activedescendant");
}

/**
 * Say which option the cursor is on, on the box and on every option.
 *
 * Both ends of one fact. A reader hearing the page is told by the box, through
 * `aria-activedescendant`, and one seeing it is told by the row, through the
 * class `modules/search.scss` draws a current row with. `aria-selected` is on
 * every option either way, the pattern wanting each of them to answer.
 *
 * `at` is -1 for a cursor that is back in the box, which is where it starts
 * and where arrowing up past the first option puts it.
 */
export function currentOption(properties: {
  /** The box, which names the current option. */
  readonly input: HTMLInputElement;

  /** The list, whose options say which of them it is. */
  readonly list: HTMLElement;

  /** Where the cursor is, counting from the first option. */
  readonly at: number;
}): void {
  const { input, list, at } = properties;
  let current: string | undefined;

  for (const [index, option] of [...list.children].entries()) {
    option.setAttribute("aria-selected", String(index === at));
    option.classList.toggle("is-current", index === at);

    if (index === at) {
      current = option.id;
    }
  }

  // Read off the option rather than rebuilt, so that the box names the element
  // that is there. A cursor back in the box names none.
  if (current === undefined) {
    input.removeAttribute("aria-activedescendant");
  } else {
    input.setAttribute("aria-activedescendant", current);
  }
}
